import { appSettings } from '@/AppSettings';
import { XHR } from '@/helpers/xhr';

export function getFileExtention(fileUrl) {
  const parts = fileUrl?.split('.') ?? [];
  return parts?.[parts.length - 1] ?? null;
}

export function mediaUrl(path, mediaURL) {
  let url = `${mediaURL ?? appSettings.get('mediaUrl')}${path}`;
  if (!url.startsWith('http')) url = `${window.location.origin}${url}`;
  return url;
}

export function imageUrl(path, params = {}, mediaURL = null) {
  if (!params.width && !params.height) {
    return mediaUrl(path, mediaURL);
  }

  const resizerUrl = appSettings.get('resizerUrl');
  if (!resizerUrl || getFileExtention(path) === 'svg') {
    return mediaUrl(path, mediaURL);
  }

  let query = [`url=${mediaUrl(path, mediaURL)}`, `fit=${params.fit || 'cover'}`];

  if (params.width) {
    query.push(`w=${params.width}`);
  }

  if (params.height) {
    query.push(`h=${params.height}`);
  }

  // Make 'url' parameter the last one. Otherwise Ant uploader won't show preview image
  query = `?${query.reverse().join('&')}`;
  return `${resizerUrl}${query}`;
}

export async function uploadFile(file, uploadUrl, onProgress = null) {
  const url = uploadUrl ?? appSettings.get('uploaderUrl');

  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file);

    XHR.post(url, {
      data: formData,
      dataType: 'formdata',
      absoluteUrl: true,
      withCredentials: false,
      onProgress,
    }).then(resolve, (error) => {
      reject(new Error(error));
    });
  });
}

// Direct-to-storage upload: ask backend for a presigned URL, PUT the file
// straight to that URL, then store the key the backend returned. Used for
// large files that would otherwise hit proxy / gateway request-time limits.
//
// Backend contract:
//   POST <presignUrl>
//     body: { filename, contentType, sizeBytes, entityType, fieldName }
//     → { uploadUrl, key, headers? }
//
// The backend uses (entityType, fieldName) to pick the right bucket / key
// strategy. The widget stores `key` as the field value after a successful PUT.
export async function uploadFileDirect(file, opts = {}) {
  const {
    presignUrl: presignUrlOverride,
    onProgress,
    entityType,
    fieldName,
    pageUrl: pageUrlOverride,
  } = opts;

  const presignUrl = presignUrlOverride || appSettings.get('uploadDirectPresignUrl');
  if (!presignUrl) {
    throw new Error('Direct upload not configured: set uploadDirectPresignUrl or pass presignUrl');
  }

  const pageUrl = pageUrlOverride || (typeof window !== 'undefined' ? window.location.href : null);

  const presignResponse = await XHR.post(presignUrl, {
    absoluteUrl: true,
    data: {
      filename: file.name,
      contentType: file.type,
      sizeBytes: file.size,
      entityType,
      fieldName,
      pageUrl,
    },
  });
  const presign =
    typeof presignResponse === 'string' ? JSON.parse(presignResponse) : presignResponse;

  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presign.uploadUrl, true);
    xhr.withCredentials = false;

    if (presign.headers && typeof presign.headers === 'object') {
      Object.entries(presign.headers).forEach(([name, value]) => {
        xhr.setRequestHeader(name, value);
      });
    } else if (file.type) {
      xhr.setRequestHeader('Content-Type', file.type);
    }

    if (typeof onProgress === 'function') {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      });
    }

    xhr.onreadystatechange = () => {
      if (xhr.readyState === XMLHttpRequest.DONE) {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Direct upload failed: ${xhr.status} ${xhr.statusText}`));
        }
      }
    };

    xhr.send(file);
  });

  return presign.key;
}
